import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
} from 'react';
import PropTypes from 'prop-types';
import axios from 'src/utils/axios';
import {
  Hidden,
  TextField,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  Alert,
  Chip,
} from '@mui/material';
import Grid from '@mui/material/Unstable_Grid2';
import { withStyles } from 'tss-react/mui';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import AssessmentIcon from '@mui/icons-material/Assessment';
import SendIcon from '@mui/icons-material/Send';
import AddLinkIcon from '@mui/icons-material/AddLink';
import LinkIcon from '@mui/icons-material/Link';
import SvgIcon from '@mui/material/SvgIcon';
import { ReactComponent as DynamicFieldsIcon } from 'src/assets/images/dynamic-fields-icon-outlined.svg';
import PopoverEmoji from 'src/components/PopoverEmoji/PopoverEmoji';
import SelectDynamicJoinFormUrl from 'src/components/SelectDynamicJoinFormUrl/SelectDynamicJoinFormUrl';
import SelectSmartLink from 'src/components/SelectSmartLink/SelectSmartLink';
import SelectFieldToInsert from 'src/components/SelectFieldToInsert/SelectFieldToInsert';
import HelpCircleWithContainer from 'src/components/HelpCircle/HelpCircleWithContainer';
import SmartComposeModal from 'src/components/SmartCompose/SmartComposeModal';
import Tooltip from 'src/components/Tooltip';

import InfoIcon from '@mui/icons-material/Info';
import textFieldEmojiStyle from 'src/components/TextFieldFormik/textFieldEmoji.style';
import { PERMISSIONS } from 'src/constants';
import { useSelector } from 'react-redux';
import helpers from 'src/helpers';

const SCORE_CHIP_COLORS = {
  GREAT: { backgroundColor: '#E8F5E9', color: '#2E7D32' },
  GOOD: { backgroundColor: '#F9FBE7', color: '#558B2F' },
  FAIR: { backgroundColor: '#FFF8E1', color: '#F57F17' },
  NEEDS_WORK: { backgroundColor: '#FFEBEE', color: '#C62828' },
};

const SCORE_CHIP_LABELS = {
  GREAT: 'Great',
  GOOD: 'Good',
  FAIR: 'Fair',
  NEEDS_WORK: 'Needs Work',
};

function TextFieldEmoji({
  title,
  classes,
  loading,
  valueName,
  currentValue,
  touchedValue = null,
  errorValue = null,
  maxTextMessageLength,
  handleBlur,
  handleChange,
  setFieldValue,
  setFieldTouched,
  defaultScreenSpacing,
  helpTextForTitle = null,
  helpTextForBlanksError = null,
  className = '',
  classNameTextField = 'textFieldRed',
  showAddEmoji = true,
  showAddDynamicField = true,
  showAddDynamicJoinFormUrl = true,
  // CON-1587 — the "Insert CP Page URL" item mirrors the Smart Form item's
  // visibility by default, so it appears wherever the Smart Form item does.
  showAddCpPageUrl = showAddDynamicJoinFormUrl,
  showAddSmartLink = true,
  showSmartFieldsTab = false,
  // CON-1621 — fail safe. A caller that turns the Smart Fields tab on but
  // forgets entityType gets Contact fields ([SFC:] resolves on every send
  // path) rather than Group fields, which silently blank outside a group.
  // Group/Event surfaces all pass their entityType explicitly.
  entityType = 'Contact',
  disabledEntityTypes = [],
  titleRightContent = null,
  screenName = null,
  isShowInfoV2 = false,
  sourceType = 'other',
  sourceId = null,
  formEntityId = null,
  allowTypeLevelEntityForms = false,
  requireSpecificEntity = false,
  perRecipientScope = 'send',
  lockPersonalized = false,
  hideFormPersonalization = false,
  // CON-1854 — starting state of "Each contact gets their own link" in the
  // Smart Form and Connect Page pickers (false for Hosts-only rules).
  defaultFormPersonalized = true,
  attachmentFileName = null,
  attachmentSizeBytes = null,
  attachmentBlobName = null,
  attachmentContainer = null,
  disableTestDeliverability = false,
  disableMessageScore = false,
  multiline = true,
  rows = 9,
}) {
  const { user } = useSelector((state) => state?.account || {});
  const [messagePosition, setMessagePosition] = useState(false);

  // Kebab menu state
  const [kebabAnchorEl, setKebabAnchorEl] = useState(null);

  // Smart Compose state
  const [smartComposeOpen, setSmartComposeOpen] = useState(false);
  const [smartComposeLoading, setSmartComposeLoading] = useState(false);
  const [scoreData, setScoreData] = useState(null);
  const [smartComposeError, setSmartComposeError] = useState(null);
  const [smartComposeInitialTab, setSmartComposeInitialTab] = useState('messageScore');

  // External-open state for picker components
  const [openDynamicField, setOpenDynamicField] = useState(false);
  const [openSmartLink, setOpenSmartLink] = useState(false);
  const [openJoinFormUrl, setOpenJoinFormUrl] = useState(false);
  // CON-1587 — separate open-state for the CP Page picker (its own menu item).
  const [openCpPageUrl, setOpenCpPageUrl] = useState(false);

  // Inline dynamic field validation state
  const [knownDynamicFields, setKnownDynamicFields] = useState(null);
  const [dynamicFieldWarnings, setDynamicFieldWarnings] = useState([]);
  const debounceTimerRef = useRef(null);

  // Auto-score chip state
  const [autoScoreData, setAutoScoreData] = useState(null);
  const lastScoredTextRef = useRef(null);
  const autoScoreTimerRef = useRef(null);

  const userRolePermissions = user?.userRole?.permissions || [];

  const canViewSmartLinks = helpers.permissions.doesUserHavePermission(
    PERMISSIONS.PERMISSIONS_SYSTEM_CODE.SMART_LINKS_VIEW,
    userRolePermissions,
  );
  const shouldShowSmartLinks = showAddSmartLink && canViewSmartLinks;

  const canViewSmartCompose = helpers.permissions.doesUserHavePermission(
    PERMISSIONS.PERMISSIONS_SYSTEM_CODE.SMART_COMPOSE_VIEW,
    userRolePermissions,
  );

  const isCanadian = user?.country === 'Canada';
  const canViewTestDeliverability = helpers.permissions.doesUserHavePermission(
    PERMISSIONS.PERMISSIONS_SYSTEM_CODE.TEST_DELIVERABILITY_VIEW,
    userRolePermissions,
  ) && !isCanadian;

  // Fetch known dynamic fields once on mount (if user has Smart Compose permission)
  useEffect(() => {
    if (!canViewSmartCompose) return undefined;
    let cancelled = false;
    axios.get('/api/message-score/dynamic-fields')
      .then((res) => {
        if (!cancelled) setKnownDynamicFields(res.data?.dynamicFields || []);
      })
      .catch(() => { /* silently ignore — inline check just won't run */ });
    return () => { cancelled = true; };
  }, [canViewSmartCompose]);

  // Check for unrecognized dynamic fields and malformed brackets (debounced)
  const checkDynamicFields = useCallback((text) => {
    if (!knownDynamicFields || !text) {
      setDynamicFieldWarnings([]);
      return;
    }

    const nextWarnings = [];

    // Check for unrecognized tokens (skip Smart Fields, Smart Links, Form URLs)
    const tokenRegex = /\[([^\]]+)\]/g;
    const matches = [...text.matchAll(tokenRegex)];
    const knownSet = new Set(knownDynamicFields);
    const knownPrefixPattern = /^\[(SFG:|SFE:|SFC:|sl:|fpg:|fpe:|fg:|fe:|fp:|f:)/i;
    matches
      .filter((match) => !knownSet.has(match[0]) && !knownPrefixPattern.test(match[0]))
      .forEach((match) => {
        nextWarnings.push(`Possible unrecognized dynamic field: ${match[0]}. Check for typos.`);
      });

    // Check for malformed brackets
    const chars = text.split('');
    let depth = 0;
    const hasMalformed = chars.some((char) => {
      if (char === '[') {
        depth += 1;
        if (depth > 1) return true;
      } else if (char === ']') {
        if (depth === 0) return true;
        depth -= 1;
      }
      return false;
    }) || depth !== 0;

    if (hasMalformed) {
      nextWarnings.push('Your message may contain an unmatched bracket [ or ]. This could prevent dynamic fields from working correctly.');
    }

    setDynamicFieldWarnings(nextWarnings);
  }, [knownDynamicFields]);

  useEffect(() => {
    if (!canViewSmartCompose || !knownDynamicFields) return undefined;
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    debounceTimerRef.current = setTimeout(() => {
      checkDynamicFields(currentValue || '');
    }, 500);
    return () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    };
  }, [currentValue, canViewSmartCompose, knownDynamicFields, checkDynamicFields]);

  // Auto-score: debounced 2s after typing stops
  useEffect(() => {
    if (!canViewSmartCompose || disableMessageScore) return undefined;
    const textToScore = currentValue || '';

    // Clear score when message is empty
    if (!textToScore.trim()) {
      setAutoScoreData(null);
      lastScoredTextRef.current = null;
      return undefined;
    }

    // Don't re-score if text hasn't changed
    if (textToScore === lastScoredTextRef.current) return undefined;

    if (autoScoreTimerRef.current) clearTimeout(autoScoreTimerRef.current);
    autoScoreTimerRef.current = setTimeout(() => {
      lastScoredTextRef.current = textToScore;
      axios.post('/api/message-score/analyze', {
        messageText: textToScore,
        attachmentFileName,
        attachmentSizeBytes,
      })
        .then((res) => { setAutoScoreData(res.data); })
        .catch(() => { /* silently ignore auto-score failures */ });
    }, 2000);
    return () => {
      if (autoScoreTimerRef.current) clearTimeout(autoScoreTimerRef.current);
    };
  }, [currentValue, canViewSmartCompose, disableMessageScore, attachmentFileName, attachmentSizeBytes]);

  const getMaxStrLen = (str, length) => {
    const newStr = str.slice(0, length);
    // The length of emoji is one or two chars. There is last char 55357 in string if we cut emoji.
    return newStr.charCodeAt(newStr.length - 1) === 55357 ? getMaxStrLen(newStr, length - 1) : newStr;
  };

  const cutMaxTextMessageLength = (value) => {
    if (value.length >= maxTextMessageLength) {
      const newValue = getMaxStrLen(value, maxTextMessageLength);
      setFieldValue(valueName, newValue, false);
    }
  };

  const saveMessageCursorPosition = (event) => {
    setMessagePosition(event?.target?.selectionStart);
  };

  const handleKeyUpCustomTextMessage = useCallback((event) => {
    const value = event?.target?.value || '';
    setFieldTouched(valueName, true);
    setMessagePosition(event?.target?.selectionStart);
    if (value.length >= maxTextMessageLength) {
      const newStr = value.slice(0, maxTextMessageLength);
      const trimmed = newStr.charCodeAt(newStr.length - 1) === 55357
        ? newStr.slice(0, maxTextMessageLength - 1)
        : newStr;
      setFieldValue(valueName, trimmed, false);
    }
  }, [valueName, maxTextMessageLength, setFieldTouched, setFieldValue]);

  const handleAddEmoji = (e, currentText) => {
    const emoji = e.native;
    // true when messagePosition isn't number maybe false or null
    const isNullCustomMessagePosition = !messagePosition && messagePosition !== 0;
    const text = isNullCustomMessagePosition
      ? `${currentText}${emoji}`
      : `${currentText.substring(0, messagePosition)}${emoji}${currentText.substring(messagePosition)}`;
    const length = !isNullCustomMessagePosition ? messagePosition + emoji.length : text.length;
    setFieldTouched(valueName, true);
    setFieldValue(valueName, text, true);
    setMessagePosition(length);
    // We show emoji and hide if it is needed after 100ms.
    setTimeout(() => {
      cutMaxTextMessageLength(text);
    }, 100);
  };

  const handleAddDynamicField = (field, currentText) => {
    const isNullCustomMessagePosition = !messagePosition && messagePosition !== 0;
    const text = isNullCustomMessagePosition
      ? `${currentText}${field}`
      : `${currentText.substring(0, messagePosition)}${field}${currentText.substring(messagePosition)}`;
    const length = !isNullCustomMessagePosition ? messagePosition + field.length : text.length;
    setFieldTouched(valueName, true);
    setFieldValue(valueName, text, true);
    setMessagePosition(length);
  };

  // Kebab menu handlers
  const handleKebabOpen = (event) => {
    setKebabAnchorEl(event.currentTarget);
  };

  const handleKebabClose = () => {
    setKebabAnchorEl(null);
  };

  // Picker menu item handlers
  const handleMenuDynamicField = () => {
    handleKebabClose();
    setOpenDynamicField(true);
  };

  const handleMenuSmartLink = () => {
    handleKebabClose();
    setOpenSmartLink(true);
  };

  const handleMenuJoinFormUrl = () => {
    handleKebabClose();
    setOpenJoinFormUrl(true);
  };

  const handleMenuCpPageUrl = () => {
    handleKebabClose();
    setOpenCpPageUrl(true);
  };

  // Smart Compose handlers
  const fetchScore = async () => {
    setSmartComposeLoading(true);
    setScoreData(null);
    setSmartComposeError(null);

    try {
      const response = await axios.post('/api/message-score/analyze', {
        messageText: currentValue || '',
        attachmentFileName,
        attachmentSizeBytes,
      });
      setScoreData(response.data);
    } catch (err) {
      console.error('Error analyzing message:', err);
      const status = err?.response?.status;
      if (status === 403) {
        setSmartComposeError('You do not have permission to use Smart Compose.');
      } else {
        setSmartComposeError('Unable to analyze message. Please try again.');
      }
    } finally {
      setSmartComposeLoading(false);
    }
  };

  const handleMessageScore = () => {
    handleKebabClose();
    setSmartComposeInitialTab('messageScore');
    setSmartComposeOpen(true);
    fetchScore();
  };

  const handleSmartComposeClose = () => {
    setSmartComposeOpen(false);
    setScoreData(null);
    setSmartComposeError(null);
  };

  const handleTestDeliverability = () => {
    handleKebabClose();
    setSmartComposeInitialTab('testDeliverability');
    setSmartComposeOpen(true);
    fetchScore();
  };

  // Open modal with auto-scored data (no re-fetch)
  const handleChipClick = () => {
    if (autoScoreData) {
      setScoreData(autoScoreData);
      setSmartComposeInitialTab('messageScore');
      setSmartComposeOpen(true);
    }
  };

  const textFieldVariant = currentValue && currentValue.includes('__________') ? 'filled' : 'outlined';

  return (
    <>
      <Grid
        className={`${classes.root} ${className}`}
        container
        spacing={defaultScreenSpacing}
      >
        <Grid xs={12} style={{ position: 'relative' }}>
          <div className={`${classes.textMessageLabel} flex`}>
            <HelpCircleWithContainer
              positionX="afterText"
              positionY="center"
              showInfo={helpTextForTitle}
              isShowInfoV2={isShowInfoV2}
            >
              <div className="text">
                {title}
              </div>
            </HelpCircleWithContainer>
            <div className="rightWrapper">
              {/* CON-1621 — caller-supplied content at the start of the
                  title row's right side, ahead of the emoji and kebab
                  icons (the Message Templates scope indicator). */}
              {titleRightContent}
              {showAddEmoji && (
                <Hidden mdDown>
                  {!loading && (
                    <PopoverEmoji
                      addEmoji={handleAddEmoji}
                      currentValue={currentValue}
                    />
                  )}
                </Hidden>
              )}
              <IconButton
                size="small"
                onClick={handleKebabOpen}
                aria-label="Tools menu"
                style={{ marginLeft: 4 }}
              >
                {smartComposeLoading
                  ? <CircularProgress size={20} />
                  : <MoreVertIcon fontSize="small" />}
              </IconButton>
              <Menu
                anchorEl={kebabAnchorEl}
                open={Boolean(kebabAnchorEl)}
                onClose={handleKebabClose}
              >
                {showAddDynamicField && (
                  <MenuItem onClick={handleMenuDynamicField}>
                    <ListItemIcon>
                      <SvgIcon component={DynamicFieldsIcon} fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Insert Dynamic Field</ListItemText>
                  </MenuItem>
                )}
                {shouldShowSmartLinks && (
                  <MenuItem onClick={handleMenuSmartLink}>
                    <ListItemIcon>
                      <LinkIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Insert Smart Link</ListItemText>
                  </MenuItem>
                )}
                {showAddDynamicJoinFormUrl && (
                  <MenuItem onClick={handleMenuJoinFormUrl}>
                    <ListItemIcon>
                      <AddLinkIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Insert Smart Form URL</ListItemText>
                  </MenuItem>
                )}
                {showAddCpPageUrl && (
                  <MenuItem onClick={handleMenuCpPageUrl}>
                    <ListItemIcon>
                      <AddLinkIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Insert Connect Page URL</ListItemText>
                  </MenuItem>
                )}
                {canViewSmartCompose && !disableMessageScore && (currentValue || '').trim() && (
                  <MenuItem onClick={handleMessageScore}>
                    <ListItemIcon>
                      <AssessmentIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Message Score</ListItemText>
                  </MenuItem>
                )}
                {canViewTestDeliverability
                  && !disableTestDeliverability
                  && ((currentValue || '').trim() || attachmentFileName || attachmentBlobName) && (
                  <MenuItem onClick={handleTestDeliverability}>
                    <ListItemIcon>
                      <SendIcon fontSize="small" />
                    </ListItemIcon>
                    <ListItemText>Test Deliverability</ListItemText>
                  </MenuItem>
                )}
              </Menu>
            </div>
          </div>
          {/* Hidden picker components — positioned at right edge so dropdowns align */}
          <div style={{
            position: 'absolute',
            right: 0,
            top: 0,
            height: 0,
            width: 0,
            overflow: 'visible',
          }}
          >
            {showAddDynamicField && (
              <SelectFieldToInsert
                addField={handleAddDynamicField}
                currentValue={currentValue}
                screenName={screenName}
                showSmartFieldsTab={showSmartFieldsTab}
                entityType={entityType}
                disabledEntityTypes={disabledEntityTypes}
                externalOpen={openDynamicField}
                onExternalOpenHandled={() => setOpenDynamicField(false)}
              />
            )}
            {shouldShowSmartLinks && (
              <SelectSmartLink
                addField={(field) => handleAddDynamicField(field, currentValue)}
                externalOpen={openSmartLink}
                onExternalOpenHandled={() => setOpenSmartLink(false)}
              />
            )}
            {showAddDynamicJoinFormUrl && (
              <SelectDynamicJoinFormUrl
                addField={handleAddDynamicField}
                currentValue={currentValue}
                screenName={screenName}
                externalOpen={openJoinFormUrl}
                onExternalOpenHandled={() => setOpenJoinFormUrl(false)}
                entityType={entityType}
                formEntityId={formEntityId}
                allowTypeLevelEntityForms={allowTypeLevelEntityForms}
                requireSpecificEntity={requireSpecificEntity}
                perRecipientScope={perRecipientScope}
                lockPersonalized={lockPersonalized}
                hideFormPersonalization={hideFormPersonalization}
                defaultPersonalized={defaultFormPersonalized}
              />
            )}
            {showAddCpPageUrl && (
              // CON-1587 — CP Page picker: no entity context (a CP Page is never
              // a join target); keeps the personalization toggle except in
              // host template contexts (hideFormPersonalization), where a
              // personalized CP Page link can never resolve either.
              <SelectDynamicJoinFormUrl
                mode="cpPage"
                addField={handleAddDynamicField}
                currentValue={currentValue}
                screenName={screenName}
                externalOpen={openCpPageUrl}
                onExternalOpenHandled={() => setOpenCpPageUrl(false)}
                lockPersonalized={lockPersonalized}
                hideFormPersonalization={hideFormPersonalization}
                defaultPersonalized={defaultFormPersonalized}
              />
            )}
          </div>
        </Grid>
      </Grid>
      <Grid
        container
        spacing={8}
      >
        <Grid
          xs={12}
        >
          {currentValue && currentValue.includes('__________') && (
            <div
              className={`${classes.warningTextBefore} flex`}
            >
              {/* <div className="icon">
            <InfoIcon />
          </div> */}
              <HelpCircleWithContainer
                positionX="afterText"
                positionY="center"
                showInfo={helpTextForBlanksError}
                color="red"
                isShowInfoV2={isShowInfoV2}
              >
                <div className="middleText">
                  Please review!&nbsp;&nbsp;&nbsp;Includes blanks!
                </div>
              </HelpCircleWithContainer>
            </div>
          )}
          <TextField
            error={Boolean(touchedValue && errorValue)}
            helperText={touchedValue && errorValue}
            label={title}
            name={valueName}
            onBlur={handleBlur}
            onChange={handleChange}
            value={currentValue}
            variant={textFieldVariant}
            color="primary"
            onClick={saveMessageCursorPosition}
            onKeyUp={handleKeyUpCustomTextMessage}
            disabled={loading}
            rows={rows}
            className={classNameTextField}
            fullWidth
            multiline={multiline}
          />
          <div
            className={`${classes.textMessageAfter} flex`}
          >
            <div className="icon">
              <InfoIcon />
            </div>
            <div className="middleText">
              {`Maximum of ${maxTextMessageLength.toLocaleString('en-US')} characters`}
            </div>
            {canViewSmartCompose && !disableMessageScore && autoScoreData && (
              <Tooltip title="Message Score: click for details" placement="top">
                <Chip
                  label={`${SCORE_CHIP_LABELS[autoScoreData.overallRating]}: ${autoScoreData.overallScore}/100`}
                  onClick={handleChipClick}
                  size="small"
                  style={{
                    cursor: 'pointer',
                    fontWeight: 500,
                    marginLeft: 'auto',
                    marginRight: 'auto',
                    ...(SCORE_CHIP_COLORS[autoScoreData.overallRating] || {}),
                  }}
                />
              </Tooltip>
            )}
            <div className="rightText">
              <div className="txt">
                Current characters:
              </div>
              {(currentValue || '').length.toLocaleString('en-US')}
            </div>
          </div>
          {dynamicFieldWarnings.length > 0 && dynamicFieldWarnings.map((warning) => (
            <Alert
              key={warning}
              severity="warning"
              style={{ marginTop: 6, fontSize: 12 }}
            >
              {warning}
            </Alert>
          ))}
        </Grid>
      </Grid>

      {(canViewSmartCompose || canViewTestDeliverability) && (
        <SmartComposeModal
          open={smartComposeOpen}
          onClose={handleSmartComposeClose}
          loading={smartComposeLoading}
          scoreData={scoreData}
          error={smartComposeError}
          sourceType={sourceType}
          sourceId={sourceId}
          initialTab={smartComposeInitialTab}
          messageBody={currentValue || ''}
          messageBodyRaw={currentValue || ''}
          attachmentFileName={attachmentFileName}
          attachmentSizeBytes={attachmentSizeBytes}
          attachmentBlobName={attachmentBlobName}
          attachmentContainer={attachmentContainer}
          canViewTestDeliverability={canViewTestDeliverability && !disableTestDeliverability}
        />
      )}
    </>
  );
}

TextFieldEmoji.propTypes = {
  title: PropTypes.string.isRequired,
  classes: PropTypes.object.isRequired,
  loading: PropTypes.bool.isRequired,
  valueName: PropTypes.string.isRequired,
  currentValue: PropTypes.string.isRequired,
  handleBlur: PropTypes.func.isRequired,
  handleChange: PropTypes.func.isRequired,
  setFieldValue: PropTypes.func.isRequired,
  setFieldTouched: PropTypes.func.isRequired,
  maxTextMessageLength: PropTypes.number.isRequired,
  defaultScreenSpacing: PropTypes.number.isRequired,
  errorValue: PropTypes.any,
  touchedValue: PropTypes.any,
  helpTextForTitle: PropTypes.node,
  helpTextForBlanksError: PropTypes.node,
  className: PropTypes.string,
  classNameTextField: PropTypes.string,
  showAddEmoji: PropTypes.bool,
  showAddDynamicField: PropTypes.bool,
  showAddDynamicJoinFormUrl: PropTypes.bool,
  showAddCpPageUrl: PropTypes.bool,
  showAddSmartLink: PropTypes.bool,
  showSmartFieldsTab: PropTypes.bool,
  entityType: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  disabledEntityTypes: PropTypes.array,
  titleRightContent: PropTypes.node,
  screenName: PropTypes.string,
  isShowInfoV2: PropTypes.bool,
  sourceType: PropTypes.string,
  sourceId: PropTypes.number,
  formEntityId: PropTypes.number,
  allowTypeLevelEntityForms: PropTypes.bool,
  requireSpecificEntity: PropTypes.bool,
  perRecipientScope: PropTypes.oneOf(['selection', 'send', 'submission']),
  lockPersonalized: PropTypes.bool,
  hideFormPersonalization: PropTypes.bool,
  defaultFormPersonalized: PropTypes.bool,
  attachmentFileName: PropTypes.string,
  attachmentSizeBytes: PropTypes.number,
  attachmentBlobName: PropTypes.string,
  attachmentContainer: PropTypes.string,
  disableTestDeliverability: PropTypes.bool,
  disableMessageScore: PropTypes.bool,
  multiline: PropTypes.bool,
  rows: PropTypes.number,
};

export default withStyles(TextFieldEmoji, textFieldEmojiStyle, { withTheme: true });
